import React, { useState } from "react";

const AIML = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Master Artificial Intelligence (AI) and Machine Learning (ML) fundamentals, algorithms, and applications, preparing for real-world projects and certifications like TensorFlow Developer or AWS Certified Machine Learning.
          <p className="font-bold text-sm">Duration:</p> 60 days, 1 hour per day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic programming knowledge (Python preferred), familiarity with mathematics (linear algebra, statistics), and an interest in data science.
          <p className="font-bold text-sm">Tools Needed:</p> A computer with Python, Jupyter Notebook, TensorFlow, Scikit-learn, NumPy, Pandas, and access to cloud platforms (e.g., Google Colab, AWS).
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to AI and ML",
          objective: "Understand the basics of AI and Machine Learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of AI, ML, and Deep Learning.</li>
                <li>Types of ML (Supervised, Unsupervised, Reinforcement).</li>
                <li>Setting up the development environment (Python, Jupyter).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Python libraries and run a sample script.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Python for AI/ML",
          objective: "Learn Python essentials for AI/ML.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Python basics (variables, loops, functions).</li>
                <li>Working with NumPy and Pandas.</li>
                <li>Data manipulation and visualization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a dataset and visualize it with Matplotlib.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Mathematics for ML – Basics",
          objective: "Understand the mathematical foundations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Linear algebra (vectors, matrices).</li>
                <li>Statistics (mean, variance, probability).</li>
                <li>Introduction to optimization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform matrix operations using NumPy.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Data Preprocessing",
          objective: "Learn to prepare data for ML models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Data cleaning (handling missing values).</li>
                <li>Feature scaling and normalization.</li>
                <li>Encoding categorical data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preprocess a dataset using Scikit-learn.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Exploratory Data Analysis (EDA)",
          objective: "Learn to explore and analyze data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding datasets (correlation, distribution).</li>
                <li>Visualization techniques (histograms, scatter plots).</li>
                <li>Identifying patterns and outliers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform EDA on a sample dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Introduction to Supervised Learning",
          objective: "Learn supervised learning concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Regression vs. classification.</li>
                <li>Train-test split and evaluation metrics (accuracy, MSE).</li>
                <li>Introduction to linear regression.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple linear regression model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Linear Regression – In-Depth",
          objective: "Master linear regression techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Multiple linear regression.</li>
                <li>Regularization (Lasso, Ridge).</li>
                <li>Model evaluation and tuning.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement multiple linear regression with regularization.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Logistic Regression",
          objective: "Learn logistic regression for classification.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding logistic function and odds.</li>
                <li>Binary and multiclass classification.</li>
                <li>Evaluation metrics (precision, recall, ROC-AUC).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a logistic regression model for classification.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Decision Trees and Random Forests",
          objective: "Learn decision tree and ensemble methods.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Decision tree algorithm (entropy, Gini).</li>
                <li>Random Forest and bagging.</li>
                <li>Hyperparameter tuning.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a Random Forest model on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Support Vector Machines (SVM)",
          objective: "Learn SVM for classification and regression.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding SVM and kernel trick.</li>
                <li>Linear and non-linear SVM.</li>
                <li>Parameter optimization (C, gamma).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement an SVM model with a kernel.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Introduction to Unsupervised Learning",
          objective: "Learn unsupervised learning concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Clustering vs. dimensionality reduction.</li>
                <li>Evaluation metrics for unsupervised learning.</li>
                <li>Introduction to K-means clustering.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Apply K-means clustering to a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: K-means and Hierarchical Clustering",
          objective: "Master clustering techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Advanced K-means (elbow method).</li>
                <li>Hierarchical clustering (dendrograms).</li>
                <li>Choosing the right number of clusters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform hierarchical clustering on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Dimensionality Reduction – PCA",
          objective: "Learn Principal Component Analysis.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding PCA and variance.</li>
                <li>Applying PCA for data reduction.</li>
                <li>Visualizing reduced dimensions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reduce dataset dimensions using PCA.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Introduction to Neural Networks",
          objective: "Learn the basics of neural networks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Neuron structure and activation functions.</li>
                <li>Feedforward and backpropagation.</li>
                <li>Setting up TensorFlow/Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple neural network with Keras.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Deep Learning – CNN Basics",
          objective: "Learn Convolutional Neural Networks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding CNN architecture (convolution, pooling).</li>
                <li>Applications in image recognition.</li>
                <li>Building a basic CNN model.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a CNN on the MNIST dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Deep Learning – RNN and LSTM",
          objective: "Learn Recurrent Neural Networks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding RNN and sequence data.</li>
                <li>Long Short-Term Memory (LSTM) networks.</li>
                <li>Applications in time series and NLP.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build an LSTM model for time series prediction.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Transfer Learning",
          objective: "Learn to use pre-trained models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding transfer learning concepts.</li>
                <li>Using pre-trained models (e.g., VGG, ResNet).</li>
                <li>Fine-tuning models for custom tasks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fine-tune a pre-trained model on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Model Evaluation and Validation",
          objective: "Learn to evaluate ML models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Cross-validation techniques.</li>
                <li>Confusion matrix and classification report.</li>
                <li>Overfitting and underfitting prevention.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Evaluate a model using cross-validation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Hyperparameter Tuning",
          objective: "Learn to optimize model hyperparameters.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Grid Search and Random Search.</li>
                <li>Using Bayesian optimization.</li>
                <li>Automating tuning with libraries (e.g., Optuna).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Tune hyperparameters for a model using Grid Search.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Feature Engineering",
          objective: "Learn to engineer features for better models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Feature selection and extraction.</li>
                <li>Creating new features from data.</li>
                <li>Handling imbalanced datasets.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Engineer features for a dataset and test performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Introduction to Reinforcement Learning",
          objective: "Learn reinforcement learning basics.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding agents, environments, and rewards.</li>
                <li>Q-learning and value iteration.</li>
                <li>Setting up a reinforcement learning environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a simple Q-learning model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Natural Language Processing (NLP) – Basics",
          objective: "Learn NLP fundamentals.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Text preprocessing (tokenization, stemming).</li>
                <li>Bag of Words and TF-IDF.</li>
                <li>Building a basic text classifier.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a text classifier using TF-IDF.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: NLP – Advanced (Word Embeddings)",
          objective: "Learn advanced NLP techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Word2Vec and GloVe embeddings.</li>
                <li>Using pre-trained embeddings.</li>
                <li>Sentiment analysis with embeddings.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform sentiment analysis with Word2Vec.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Deploying ML Models",
          objective: "Learn to deploy ML models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Flask and FastAPI.</li>
                <li>Serializing models with Pickle/Joblib.</li>
                <li>Deploying a model to a web app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a simple model using Flask.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Cloud ML with AWS/Google Cloud",
          objective: "Learn cloud-based ML deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to AWS SageMaker/Google AI Platform.</li>
                <li>Training models on the cloud.</li>
                <li>Deploying cloud-hosted models.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a model on Google Colab or AWS SageMaker.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Time Series Analysis",
          objective: "Learn to analyze time series data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding time series components.</li>
                <li>ARIMA and SARIMA models.</li>
                <li>Forecasting with time series.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build an ARIMA model for forecasting.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Anomaly Detection",
          objective: "Learn to detect anomalies in data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding anomaly detection techniques.</li>
                <li>Isolation Forest and One-Class SVM.</li>
                <li>Applications in fraud detection.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Detect anomalies using Isolation Forest.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Ensemble Methods",
          objective: "Learn advanced ensemble techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Boosting (AdaBoost, Gradient Boosting).</li>
                <li>XGBoost and LightGBM.</li>
                <li>Stacking and blending models.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train an XGBoost model on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., Image Classifier).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Development (Part 1)",
          objective: "Develop the initial setup of the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the project environment.</li>
                <li>Collecting and preprocessing data.</li>
                <li>Implementing initial model architecture.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Final Project – Development (Part 2)",
          objective: "Continue developing the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding advanced features (e.g., CNN, LSTM).</li>
                <li>Optimizing model performance.</li>
                <li>Testing configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the project with additional features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Final Project – Testing and Debugging",
          objective: "Test and debug the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing model accuracy and robustness.</li>
                <li>Troubleshooting training issues.</li>
                <li>Optimizing hyperparameters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Final Project – Documentation",
          objective: "Document the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting code and model architecture.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Preparing for Certification – Basics",
          objective: "Review foundational ML concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review supervised and unsupervised learning.</li>
                <li>Practice data preprocessing and EDA.</li>
                <li>Mock scenarios for basics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock basic ML task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Preparing for Certification – Deep Learning",
          objective: "Review deep learning skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review CNNs, RNNs, and transfer learning.</li>
                <li>Practice model training and tuning.</li>
                <li>Mock deep learning scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a mock CNN model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Preparing for Certification – Deployment",
          objective: "Review deployment skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review model serialization and web deployment.</li>
                <li>Practice cloud deployment tasks.</li>
                <li>Mock deployment scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a mock model to a web app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo walkthrough.</li>
                <li>Anticipating questions and challenges.</li>
                <li>Practicing the presentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the project demo.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Peer Review",
          objective: "Receive feedback on the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Presenting to peers for feedback.</li>
                <li>Discussing improvements and optimizations.</li>
                <li>Finalizing the project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present and refine the project based on feedback.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Exam Strategy and Tips",
          objective: "Learn strategies for ML certifications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding certification formats (e.g., TensorFlow, AWS).</li>
                <li>Time management and prioritization tips.</li>
                <li>Reviewing common pitfalls and solutions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock exam task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Mock Exam – Supervised Learning",
          objective: "Practice supervised learning tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Build a regression/classification model.</li>
                <li>Review evaluation metrics.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock supervised learning task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Mock Exam – Unsupervised Learning",
          objective: "Practice unsupervised learning tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Apply clustering or PCA.</li>
                <li>Review dimensionality reduction techniques.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock unsupervised learning task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Mock Exam – Deep Learning",
          objective: "Practice deep learning tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Build a CNN or RNN.</li>
                <li>Review transfer learning and tuning.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock deep learning task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Final Adjustments",
          objective: "Finalize the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reviewing project requirements.</li>
                <li>Making final tweaks and optimizations.</li>
                <li>Preparing for presentation day.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Finalize the project setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Presentation Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practicing the full demo.</li>
                <li>Anticipating questions from the audience.</li>
                <li>Refining the presentation flow.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the presentation with a timer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 46: Reinforcement Learning – Advanced",
          objective: "Deepen reinforcement learning knowledge.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deep Q-Networks (DQN).</li>
                <li>Policy gradients and actor-critic methods.</li>
                <li>Applications in gaming and robotics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a DQN model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 47: AutoML and Model Selection",
          objective: "Learn automated machine learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to AutoML (e.g., H2O.ai, Auto-sklearn).</li>
                <li>Automated feature engineering and model selection.</li>
                <li>Evaluating AutoML results.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Use Auto-sklearn to train a model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 48: Ethical AI and Bias Mitigation",
          objective: "Understand ethical considerations in AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Ethical challenges in AI (bias, fairness).</li>
                <li>Techniques to mitigate bias in models.</li>
                <li>Case studies on ethical AI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Analyze and mitigate bias in a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 49: Real-Time ML Applications",
          objective: "Learn to build real-time ML systems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to real-time ML pipelines.</li>
                <li>Using Kafka or MQTT for streaming data.</li>
                <li>Deploying real-time models.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Simulate a real-time prediction system.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 50: Capstone Project – Planning",
          objective: "Plan a capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the capstone project.</li>
                <li>Suggested project ideas (e.g., Chatbot).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a capstone project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 51: Capstone Project – Development (Part 1)",
          objective: "Develop the initial setup of the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the project environment.</li>
                <li>Collecting and preprocessing data.</li>
                <li>Implementing initial model architecture.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the capstone project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 52: Capstone Project – Development (Part 2)",
          objective: "Continue developing the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding advanced features (e.g., NLP, real-time).</li>
                <li>Optimizing model performance.</li>
                <li>Testing configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the capstone project with additional features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 53: Capstone Project – Testing and Debugging",
          objective: "Test and debug the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing model accuracy and robustness.</li>
                <li>Troubleshooting training issues.</li>
                <li>Optimizing hyperparameters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the capstone project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 54: Capstone Project – Documentation",
          objective: "Document the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting code and model architecture.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the capstone project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 55: Capstone Project – Rehearsal",
          objective: "Rehearse the capstone project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo walkthrough.</li>
                <li>Anticipating questions and challenges.</li>
                <li>Practicing the presentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the capstone project demo.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 56: Capstone Project – Peer Review",
          objective: "Receive feedback on the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Presenting to peers for feedback.</li>
                <li>Discussing improvements and optimizations.</li>
                <li>Finalizing the project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present and refine the capstone project based on feedback.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 57: Capstone Project – Final Adjustments",
          objective: "Finalize the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reviewing project requirements.</li>
                <li>Making final tweaks and optimizations.</li>
                <li>Preparing for presentation day.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Finalize the capstone project setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 58: Capstone Project – Presentation Rehearsal",
          objective: "Rehearse the capstone project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practicing the full demo.</li>
                <li>Anticipating questions from the audience.</li>
                <li>Refining the presentation flow.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the presentation with a timer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 59: Capstone Project – Presentation",
          objective: "Present the capstone project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed capstone project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 60: Course Review and Next Steps",
          objective: "Review course content and plan future learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Recap of key AI/ML concepts.</li>
                <li>Preparing for certifications (TensorFlow, AWS).</li>
                <li>Exploring advanced topics (e.g., GANs, reinforcement learning).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss personal projects and career paths.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Understand and implement supervised, unsupervised, and reinforcement learning.</li>
            <li>Build and deploy deep learning models using TensorFlow and Keras.</li>
            <li>Perform data preprocessing, feature engineering, and model evaluation.</li>
            <li>Deploy ML models to web and cloud platforms.</li>
            <li>Prepare for AI/ML certifications and real-world applications.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Image Classifier:</span> Build a CNN-based image classification system using transfer learning.
            </li>
            <li>
              <span className="font-bold text-sm">Chatbot:</span> Develop an NLP-based chatbot using LSTM and deployment with Flask.
            </li>
            <li>
              <span className="font-bold text-sm">Real-Time Anomaly Detection:</span> Create a system to detect anomalies in streaming data using Isolation Forest.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Practice with Kaggle datasets and TensorFlow tutorials.</li>
            <li>Review certification objectives (e.g., TensorFlow Developer, AWS ML) regularly.</li>
            <li>Use discussion time for troubleshooting complex models and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus covers a comprehensive AI/ML curriculum over 60 days, preparing learners for certifications and professional roles.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-gray-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Mastering AI & Machine Learning Syllabus
        </h1>
        <p className="text-sm text-white">(60 Hours – 1 Hour Per Day)</p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIML;